import { useState, useMemo } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Filter, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export interface ImportError {
  rowNumber: number;
  columnName: string;
  receivedValue: string;
  rule: string;
  message: string;
}

interface ErrorGroup {
  rowNumber: number;
  errors: ImportError[];
}

interface ErrorStats {
  totalErrors: number;
  affectedRows: number;
  errorsByField: Record<string, number>;
  errorsByType: Record<string, number>;
}

interface ImportErrorDetailsProps {
  errors: ImportError[];
}

/**
 * Componente de exibição detalhada de erros de validação
 * Agrupa erros por linha, mostra estatísticas e oferece sugestões de correção
 */
export default function ImportErrorDetails({ errors = [] }: ImportErrorDetailsProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [filterField, setFilterField] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'linha' | 'campo' | 'tipo'>('linha');

  // Agrupar erros por linha
  const groupedErrors = useMemo(() => {
    const groups: Record<number, ImportError[]> = {};
    
    errors.forEach((error) => {
      if (!groups[error.rowNumber]) {
        groups[error.rowNumber] = [];
      }
      groups[error.rowNumber].push(error);
    });

    return Object.entries(groups)
      .map(([rowNumber, errs]) => ({
        rowNumber: parseInt(rowNumber),
        errors: errs,
      }))
      .sort((a, b) => a.rowNumber - b.rowNumber);
  }, [errors]);

  // Calcular estatísticas
  const stats = useMemo((): ErrorStats => {
    const errorsByField: Record<string, number> = {};
    const errorsByType: Record<string, number> = {};

    errors.forEach((error) => {
      // Contar por campo
      errorsByField[error.columnName] = (errorsByField[error.columnName] || 0) + 1;

      // Contar por tipo de erro (extrair tipo da mensagem ou regra)
      const errorType = extractErrorType(error.rule);
      errorsByType[errorType] = (errorsByType[errorType] || 0) + 1;
    });

    return {
      totalErrors: errors.length,
      affectedRows: groupedErrors.length,
      errorsByField,
      errorsByType,
    };
  }, [errors, groupedErrors]);

  // Extrair tipo de erro da regra
  function extractErrorType(rule: string): string {
    if (rule.includes('required') || rule.includes('obrigatório')) return 'Campo Obrigatório';
    if (rule.includes('format') || rule.includes('formato')) return 'Formato Inválido';
    if (rule.includes('length') || rule.includes('comprimento')) return 'Comprimento Inválido';
    if (rule.includes('range') || rule.includes('intervalo')) return 'Valor Fora do Intervalo';
    if (rule.includes('unique') || rule.includes('único')) return 'Valor Duplicado';
    if (rule.includes('type') || rule.includes('tipo')) return 'Tipo Inválido';
    return 'Outro Erro';
  }

  // Gerar sugestão de correção
  function getSuggestion(error: ImportError): string {
    const rule = error.rule.toLowerCase();
    const field = error.columnName;

    if (rule.includes('required') || rule.includes('obrigatório')) {
      return `O campo "${field}" é obrigatório. Preencha com um valor válido.`;
    }
    if (rule.includes('format') || rule.includes('formato')) {
      return `O campo "${field}" tem um formato inválido. Verifique o formato esperado no dicionário.`;
    }
    if (rule.includes('length') || rule.includes('comprimento')) {
      return `O campo "${field}" excede o comprimento máximo permitido. Reduza o texto.`;
    }
    if (rule.includes('range') || rule.includes('intervalo')) {
      return `O valor "${error.receivedValue}" está fora do intervalo permitido para "${field}".`;
    }
    if (rule.includes('unique') || rule.includes('único')) {
      return `O valor "${error.receivedValue}" já existe para "${field}". Use um valor único.`;
    }
    if (rule.includes('type') || rule.includes('tipo')) {
      return `O campo "${field}" deve ser do tipo correto (verifique se é número, texto, etc.).`;
    }

    return `Verifique o valor do campo "${field}" e consulte o dicionário de campos.`;
  }

  // Filtrar erros
  const filteredErrors = useMemo(() => {
    if (!filterField) return groupedErrors;
    return groupedErrors.map((group) => ({
      ...group,
      errors: group.errors.filter((e) => e.columnName === filterField),
    })).filter((group) => group.errors.length > 0);
  }, [groupedErrors, filterField]);

  // Campos únicos com erros
  const fieldsWithErrors = useMemo(() => {
    return Object.keys(stats.errorsByField).sort();
  }, [stats]);

  // Toggle expansão de linha
  const toggleRowExpanded = (rowNumber: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowNumber)) {
      newExpanded.delete(rowNumber);
    } else {
      newExpanded.add(rowNumber);
    }
    setExpandedRows(newExpanded);
  };

  // Exportar erros como CSV
  const handleExportErrors = () => {
    const csv = [
      ['Linha', 'Campo', 'Valor Recebido', 'Tipo de Erro', 'Mensagem'].join(','),
      ...errors.map((e) => [
        e.rowNumber,
        e.columnName,
        `"${e.receivedValue.replace(/"/g, '""')}"`,
        extractErrorType(e.rule),
        `"${e.message.replace(/"/g, '""')}"`,
      ].join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `erros_importacao_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (errors.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Card de Estatísticas */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo de Erros</CardTitle>
          <CardDescription>Análise detalhada dos erros encontrados</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Total de Erros */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="text-sm text-red-700 mb-1">Total de Erros</div>
              <div className="text-2xl font-bold text-red-900">{stats.totalErrors}</div>
            </div>

            {/* Linhas Afetadas */}
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="text-sm text-orange-700 mb-1">Linhas Afetadas</div>
              <div className="text-2xl font-bold text-orange-900">{stats.affectedRows}</div>
            </div>

            {/* Campos com Erro */}
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="text-sm text-yellow-700 mb-1">Campos com Erro</div>
              <div className="text-2xl font-bold text-yellow-900">{fieldsWithErrors.length}</div>
            </div>

            {/* Taxa de Erro */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-700 mb-1">Erros por Linha</div>
              <div className="text-2xl font-bold text-slate-900">
                {(stats.totalErrors / stats.affectedRows).toFixed(1)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Distribuição de Erros por Tipo */}
      {Object.keys(stats.errorsByType).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Tipo de Erro</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats.errorsByType)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <span className="text-sm font-medium text-slate-700">{type}</span>
                    <span className="text-sm font-bold text-slate-900">{count}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtro e Exportação */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros e Ações</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-3">
            {/* Filtro por Campo */}
            {fieldsWithErrors.length > 0 && (
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  <Filter className="w-4 h-4 inline mr-2" />
                  Filtrar por Campo
                </label>
                <select
                  value={filterField || ''}
                  onChange={(e) => setFilterField(e.target.value || null)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                >
                  <option value="">Todos os campos</option>
                  {fieldsWithErrors.map((field) => (
                    <option key={field} value={field}>
                      {field} ({stats.errorsByField[field]})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Exportar */}
            <div className="flex items-end">
              <Button
                onClick={handleExportErrors}
                variant="outline"
                className="w-full md:w-auto"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Erros (CSV)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista Detalhada de Erros por Linha */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhes dos Erros</CardTitle>
          <CardDescription>
            {filterField
              ? `Mostrando erros do campo "${filterField}"`
              : `${filteredErrors.length} linha(s) com erro(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {filteredErrors.length > 0 ? (
              filteredErrors.map((group) => (
                <Card key={group.rowNumber} className="border-red-200 bg-red-50">
                  <CardContent className="pt-4">
                    {/* Header da Linha */}
                    <button
                      onClick={() => toggleRowExpanded(group.rowNumber)}
                      className="w-full text-left flex items-center justify-between hover:bg-red-100 p-2 rounded transition"
                    >
                      <div>
                        <div className="font-semibold text-red-900">
                          Linha {group.rowNumber}
                        </div>
                        <div className="text-sm text-red-700">
                          {group.errors.length} erro(s) encontrado(s)
                        </div>
                      </div>
                      {expandedRows.has(group.rowNumber) ? (
                        <ChevronUp className="w-4 h-4 text-red-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-red-600" />
                      )}
                    </button>

                    {/* Detalhes Expandidos */}
                    {expandedRows.has(group.rowNumber) && (
                      <div className="mt-4 space-y-3 border-t border-red-200 pt-3">
                        {group.errors.map((error, idx) => (
                          <div
                            key={idx}
                            className="p-3 bg-white rounded border border-red-100 space-y-2"
                          >
                            {/* Campo e Tipo de Erro */}
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="font-semibold text-slate-900">
                                  {error.columnName}
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                  Tipo: {extractErrorType(error.rule)}
                                </div>
                              </div>
                              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-1" />
                            </div>

                            {/* Mensagem de Erro */}
                            <div className="text-sm text-red-700 font-medium">
                              {error.message}
                            </div>

                            {/* Valor Recebido */}
                            <div className="text-xs bg-slate-100 p-2 rounded">
                              <span className="font-semibold text-slate-700">Valor recebido:</span>
                              <div className="text-slate-600 break-words mt-1">
                                {error.receivedValue || '(vazio)'}
                              </div>
                            </div>

                            {/* Regra */}
                            <div className="text-xs bg-slate-100 p-2 rounded">
                              <span className="font-semibold text-slate-700">Regra de validação:</span>
                              <div className="text-slate-600 mt-1">{error.rule}</div>
                            </div>

                            {/* Sugestão de Correção */}
                            <Alert className="border-blue-200 bg-blue-50">
                              <AlertCircle className="h-4 w-4 text-blue-600" />
                              <AlertDescription className="text-blue-800 text-xs">
                                {getSuggestion(error)}
                              </AlertDescription>
                            </Alert>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <Alert className="border-blue-200 bg-blue-50">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Nenhum erro encontrado com os filtros selecionados.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabela Resumida */}
      <Card>
        <CardHeader>
          <CardTitle>Tabela de Erros</CardTitle>
          <CardDescription>Visualização em tabela de todos os erros</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Linha</TableHead>
                  <TableHead>Campo</TableHead>
                  <TableHead>Valor Recebido</TableHead>
                  <TableHead>Tipo de Erro</TableHead>
                  <TableHead>Mensagem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredErrors.map((group) =>
                  group.errors.map((error, idx) => (
                    <TableRow key={`${group.rowNumber}-${idx}`}>
                      <TableCell className="font-medium">{error.rowNumber}</TableCell>
                      <TableCell className="font-medium text-slate-700">
                        {error.columnName}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                        {error.receivedValue || '(vazio)'}
                      </TableCell>
                      <TableCell className="text-sm">
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                          {extractErrorType(error.rule)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                        {error.message}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
